import { motion } from 'motion/react';
import { twJoin } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { ModularCard } from '@/components/ui/custom/modular/ModularCard';
import useToggle from '@/data/hook/useToggle';
import { CodeValidationResult } from '@/models/CodeValidationResult';
import { CodeValidationResultEnum } from '@/models/CodeValidationResult';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { parseInputs } from '@/utils/codeParser';
import { LanguageCodeEditorEnum } from '@/models/CodeEditorActivity';

type CodeResultProps = {
  result: CodeValidationResult;
  index: number;
  status: CodeValidationResultEnum;
  language: LanguageCodeEditorEnum;
  sourceCode: string;
};

function codeResultStyle(status: CodeValidationResultEnum) {
  const backgroundColor =
    status === CodeValidationResultEnum.ACCEPTED
      ? 'bg-success-content'
      : 'bg-error-content';
  const textColor =
    status === CodeValidationResultEnum.ACCEPTED
      ? 'text-success'
      : 'text-error';
  const message =
    status === CodeValidationResultEnum.ACCEPTED
      ? t('codeValidation.passed')
      : t('codeValidation.failed');

  return { backgroundColor, textColor, message };
}

export default function CodeResult(props: CodeResultProps) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'codeValidation',
  });

  const { result, index, status, language, sourceCode } = props;

  const order = index + 1;

  const { isOpen, toggle } = useToggle(
    status === CodeValidationResultEnum.WRONG_ANSWER,
  );

  const { backgroundColor, textColor, message } = codeResultStyle(
    result.status.description,
  );

  const parsedCode = parseInputs(sourceCode, result.stdin, language);

  return (
    <motion.li
      data-testid={`codeResult${index}`}
      key={result.status.id}
      initial={{ opacity: 0, x: -5 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <ModularCard
        className={{
          base: twJoin(
            'flex gap-2 rounded-2xl border-none shadow-none p-4',
            backgroundColor,
          ),
        }}
      >
        <ModularCard.Header
          hideTooltip
          title={`${t('test')} ${order}`}
          onOpen={toggle}
          isOpen={isOpen}
          className={{
            base: 'p-0 ',
            title: textColor,
            button: twJoin('right-0', textColor),
          }}
        />
        <ModularCard.Accordion
          isOpen={isOpen}
          className={{
            base: twJoin('flex flex-col gap-2', textColor),
          }}
        >
          <ConditionalRenderer
            condition={!result.stderr}
            fallback={
              <code className="bg-neutral text-base-100 rounded-md p-2">
                <pre>{result.stderr}</pre>
              </code>
            }
          >
            <section className="flex flex-col gap-2">
              <section className="flex flex-col gap-2">
                <Text text={t('passedValues')} />
                <code className="bg-neutral text-base-100 rounded-md p-2">
                  <pre>{parsedCode}</pre>
                </code>
              </section>

              <ConditionalRenderer condition={result.expectedOutput}>
                <Text text={`${t('expect')}: ${result.expectedOutput}`} />
              </ConditionalRenderer>
              <ConditionalRenderer condition={result.stdout}>
                <Text text={`${t('returned')}: ${result.stdout}`} />
              </ConditionalRenderer>
            </section>
          </ConditionalRenderer>

          <Text text={`${message}!`} className="font-500" />
        </ModularCard.Accordion>
      </ModularCard>
    </motion.li>
  );
}
